import React, { useState, useEffect } from 'react';
import {
  Lock, User, Eye, EyeOff, LogOut, MessageSquare, Database, Upload,
  Search, Filter, Calendar, Edit2, Pause, Play, Trash2, AlertTriangle,
  Check, X, ChevronLeft, ChevronRight, RefreshCw, Download, FileText,
  File, FileSpreadsheet, Image, Link, FolderOpen, Plus, Eye as EyeIcon,
  Menu, X as CloseIcon, Square, CircleDot, Globe, BookOpen, Loader,
  Wrench, ArrowRight, ArrowDown, HelpCircle, Settings, Copy, Save
} from 'lucide-react';
import AIChat from '../components/AIChat';

// Credenciales para acceso a XIA
const XIA_USERS = {
  'xia': { password: 'manantial2026', role: 'admin' },
  'admin': { password: 'infoticketing', role: 'admin' },
  'trainer': { password: 'trainer@com', role: 'admin' },
  'demo': { password: 'demo123', role: 'user' }
};

// Departamentos de la empresa
const DEPARTMENTS = [
  { id: 'administracion', name: 'ADMINISTRACIÓN', icon: '📊' },
  { id: 'gerencia', name: 'GERENCIA', icon: '👔' },
  { id: 'entradas', name: 'ENTRADAS', icon: '🎫' },
  { id: 'sat', name: 'SAT', icon: '🔧' },
  { id: 'diseno', name: 'DISEÑO', icon: '🎨' },
];

// API base URL
const API_URL = process.env.REACT_APP_API_URL || '';

export default function XIAStandalone() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' | 'datos' | 'script' | 'logs'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Estado para logs
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    department: 'all',
    learned: 'all',
    hasQuestions: 'all',
    dateFrom: '',
    dateTo: ''
  });

  // Estado para edición/eliminación
  const [editingLog, setEditingLog] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteStep, setDeleteStep] = useState(0);
  const [viewingQuestions, setViewingQuestions] = useState(null);

  // Estado para subida de archivos
  const [uploadMode, setUploadMode] = useState('file'); // 'file' | 'drive' | 'url'
  const [selectedDepartment, setSelectedDepartment] = useState('administracion');
  const [selectedTipo, setSelectedTipo] = useState('conocimiento'); // 'conocimiento' | 'logica'
  const [selectedPriority, setSelectedPriority] = useState(5); // 1-10 (10 = máxima prioridad)
  const [driveUrl, setDriveUrl] = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]); // Cola de subida ordenada por prioridad
  const [uploadStats, setUploadStats] = useState({
    injectionStartDate: new Date().toISOString(), // Fecha real de inicio
    totalInjected: 0,
    totalUploaded: 0,
    totalPending: 0
  });

  // Estado del script de inyección automática
  const [injectionStatus, setInjectionStatus] = useState('stopped'); // 'running' | 'paused' | 'stopped'
  const [injectionLoading, setInjectionLoading] = useState(false);
  const [scriptLogs, setScriptLogs] = useState([]);
  const [scriptStartTime, setScriptStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Estado del scraping
  const [scrapingTopic, setScrapingTopic] = useState('');
  const [scrapingDepartment, setScrapingDepartment] = useState('sat');

  // Estado para añadir URLs manualmente
  const [newUrl, setNewUrl] = useState('');
  const [newUrlDepartment, setNewUrlDepartment] = useState('sat');
  const [newUrlAuth, setNewUrlAuth] = useState({ user: '', pass: '' });
  const [showUrlAuth, setShowUrlAuth] = useState(false);
  const [addingUrl, setAddingUrl] = useState(false);
  const [crawlMode, setCrawlMode] = useState(true); // Modo crawler completo por defecto
  const [scrapingStatus, setScrapingStatus] = useState('idle'); // 'idle' | 'searching' | 'found' | 'loading'
  const [scrapingUrls, setScrapingUrls] = useState([]);
  const [scrapingSelected, setScrapingSelected] = useState([]);
  const [scrapingProgress, setScrapingProgress] = useState({ current: 0, total: 0 });

  // Estado para agentes/conocimiento cargado
  const [faqStats, setFaqStats] = useState({ totalEnCache: 0, preguntasAsimiladas: 0, porDepartamento: {} });
  const [loadedAgents, setLoadedAgents] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);

  // Estado para Google Drive
  const [driveStatus, setDriveStatus] = useState({ hasCredentials: false, active: false });
  const [showDriveConfig, setShowDriveConfig] = useState(false);
  const [driveCredentials, setDriveCredentials] = useState('');

  // Estado para diagnósticos
  const [diagnostics, setDiagnostics] = useState([]);
  const [diagnosticsLoading, setDiagnosticsLoading] = useState(false);
  const [editingDiagnostic, setEditingDiagnostic] = useState(null); // null = lista, objeto = editando
  const [newDiagnostic, setNewDiagnostic] = useState({
    nombre: '',
    keywords: [],
    pasos: {
      inicio: {
        pregunta: '',
        opciones: [
          { texto: 'Sí', siguiente: '' },
          { texto: 'No', siguiente: '' },
          { texto: 'No lo sé', siguiente: '' }
        ]
      }
    }
  });
  const [selectedStep, setSelectedStep] = useState('inicio'); // Paso seleccionado para editar
  const [newKeyword, setNewKeyword] = useState('');

  // Verificar sesión guardada
  useEffect(() => {
    const savedAuth = localStorage.getItem('xia_auth');
    if (savedAuth) {
      try {
        const auth = JSON.parse(savedAuth);
        if (auth.expires > Date.now()) {
          setIsAuthenticated(true);
          setCurrentUser(auth);
        } else {
          localStorage.removeItem('xia_auth');
        }
      } catch (e) {
        localStorage.removeItem('xia_auth');
      }
    }
    setLoading(false);
  }, []);

  // Cargar logs cuando se cambia a pestaña de logs
  useEffect(() => {
    if (activeTab === 'logs' && isAuthenticated) {
      fetchLogs();
    }
  }, [activeTab, page, filters.department, filters.learned, filters.hasQuestions]);

  // Cargar estado del script y fuentes
  useEffect(() => {
    if (activeTab === 'script' && isAuthenticated) {
      console.log('Cargando datos del script...');
      fetchScriptStatus();
      fetchAllSources();
      fetchFaqStats(); // Cargar agentes/conocimiento
    }
  }, [activeTab, isAuthenticated]);

  // Polling del estado del script cada 5 segundos si está en la pestaña script
  useEffect(() => {
    let interval;
    if (activeTab === 'script' && isAuthenticated && injectionStatus === 'running') {
      interval = setInterval(() => {
        fetchScriptStatus();
        fetchAllSources();
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [activeTab, injectionStatus]);

  // Timer para mostrar tiempo transcurrido
  useEffect(() => {
    let timer;
    if (injectionStatus === 'running' && scriptStartTime) {
      timer = setInterval(() => {
        setElapsedTime(Math.floor((Date.now() - new Date(scriptStartTime).getTime()) / 1000));
      }, 1000);
    } else if (injectionStatus === 'stopped') {
      setElapsedTime(0);
    }
    return () => clearInterval(timer);
  }, [injectionStatus, scriptStartTime]);

  // Cargar estado de Google Drive
  useEffect(() => {
    if (activeTab === 'datos' && isAuthenticated) {
      fetchDriveStatus();
    }
  }, [activeTab, isAuthenticated]);

  const fetchDriveStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/xia-training/drive/status`);
      if (response.ok) {
        const data = await response.json();
        setDriveStatus(data);
      }
    } catch (err) {
      console.error('Error fetching Drive status:', err);
    }
  };

  const saveDriveCredentials = async () => {
    try {
      const credentials = JSON.parse(driveCredentials);
      const response = await fetch(`${API_URL}/api/xia-training/drive/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credentials })
      });

      if (response.ok) {
        setShowDriveConfig(false);
        setDriveCredentials('');
        fetchDriveStatus();
        alert('Credenciales guardadas correctamente');
      } else {
        const error = await response.json();
        alert(`Error: ${error.error || 'Error desconocido'}`);
      }
    } catch (err) {
      alert('Error: El JSON de credenciales no es válido');
    }
  };

  // Formatear tiempo transcurrido
  const formatElapsedTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`;
    } else if (mins > 0) {
      return `${mins}m ${secs}s`;
    }
    return `${secs}s`;
  };

  const fetchScriptStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/xia-training/script/status`);
      if (response.ok) {
        const data = await response.json();
        setInjectionStatus(data.status || 'stopped');
        if (data.logs) setScriptLogs(data.logs);
        if (data.startTime) setScriptStartTime(data.startTime);
      }
    } catch (err) {
      console.error('Error fetching script status:', err);
    }
  };

  const fetchAllSources = async () => {
    try {
      console.log('Fetching sources from API...');
      const response = await fetch(`${API_URL}/api/xia-training/sources`);
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Sources data:', data);
        setUploadedFiles(data.sources || []);
        setUploadStats(prev => ({
          ...prev,
          totalUploaded: data.totalUploaded || 0,
          totalInjected: data.totalInjected || 0,
          totalPending: data.totalPending || 0
        }));
      }
    } catch (err) {
      console.error('Error fetching sources:', err);
    }
  };

  // Cargar estadísticas de agentes/conocimiento
  const fetchFaqStats = async () => {
    setAgentsLoading(true);
    try {
      // Obtener estadísticas FAQ
      const statsRes = await fetch(`${API_URL}/api/xia-training/faq-stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setFaqStats(statsData.stats || { totalEnCache: 0, preguntasAsimiladas: 0, porDepartamento: {} });
      }

      // Obtener records/agentes cargados
      const recordsRes = await fetch(`${API_URL}/api/xia-training/records?limit=100`);
      if (recordsRes.ok) {
        const recordsData = await recordsRes.json();
        setLoadedAgents(recordsData.records || []);
      }
    } catch (err) {
      console.error('Error fetching FAQ stats:', err);
    }
    setAgentsLoading(false);
  };

  const handleScriptControl = async (action) => {
    setInjectionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/xia-training/script/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (response.ok) {
        const data = await response.json();
        setInjectionStatus(data.status);
        // Añadir log
        setScriptLogs(prev => [{
          time: new Date().toLocaleTimeString(),
          action: action.toUpperCase(),
          message: data.message
        }, ...prev].slice(0, 50));
      }
    } catch (err) {
      console.error('Error controlling script:', err);
    }
    setInjectionLoading(false);
  };

  // Función para añadir URL a la cola
  const handleAddUrl = async () => {
    if (!newUrl.trim()) return;

    setAddingUrl(true);
    try {
      const body = {
        urls: [newUrl.trim()],
        department: newUrlDepartment,
        source_type: 'reviewer', // Marcado como fuente de revisor (manual)
        crawl_mode: crawlMode // Modo crawler completo
      };

      // Añadir autenticación si está configurada
      if (showUrlAuth && newUrlAuth.user && newUrlAuth.pass) {
        body.auth = { user: newUrlAuth.user, pass: newUrlAuth.pass };
      }

      const response = await fetch(`${API_URL}/api/xia-training/script/queue`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (response.ok) {
        const data = await response.json();
        // Añadir log
        setScriptLogs(prev => [{
          time: new Date().toLocaleTimeString(),
          action: 'QUEUE',
          message: `URL añadida: ${newUrl.substring(0, 50)}... (${newUrlDepartment})`
        }, ...prev].slice(0, 50));

        // Limpiar formulario
        setNewUrl('');
        setNewUrlAuth({ user: '', pass: '' });
        setShowUrlAuth(false);

        // Refrescar fuentes
        fetchAllSources();
        fetchScriptStatus();
      }
    } catch (err) {
      console.error('Error adding URL:', err);
    }
    setAddingUrl(false);
  };

  // Funciones de Scraping
  const handleSearchScraping = async () => {
    console.log('🔍 handleSearchScraping llamado, topic:', scrapingTopic);
    if (!scrapingTopic.trim()) {
      console.log('❌ Topic vacío, saliendo');
      return;
    }

    setScrapingStatus('searching');
    setScrapingUrls([]);
    setScrapingSelected([]);

    try {
      console.log('📡 Enviando petición a:', `${API_URL}/api/xia-training/scraping/search`);
      const response = await fetch(`${API_URL}/api/xia-training/scraping/search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: scrapingTopic,
          department: scrapingDepartment
        })
      });

      console.log('📥 Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ URLs encontradas:', data.urls?.length || 0, data);
        setScrapingUrls(data.urls || []);
        setScrapingSelected(data.urls?.map(u => u.url) || []);
        setScrapingStatus('found');
      } else {
        console.log('❌ Response no ok:', response.status);
        setScrapingStatus('idle');
      }
    } catch (err) {
      console.error('💥 Error searching:', err);
      setScrapingStatus('idle');
    }
  };

  const handleLoadToXIA = async () => {
    if (scrapingSelected.length === 0) return;

    setScrapingStatus('loading');
    setScrapingProgress({ current: 0, total: scrapingSelected.length });

    try {
      const response = await fetch(`${API_URL}/api/xia-training/scraping/load`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          urls: scrapingSelected,
          department: scrapingDepartment
        })
      });

      if (response.ok) {
        const data = await response.json();
        setScrapingProgress({ current: data.loaded || 0, total: scrapingSelected.length });
        // Limpiar y volver a estado inicial
        setTimeout(() => {
          setScrapingStatus('idle');
          setScrapingUrls([]);
          setScrapingSelected([]);
          setScrapingTopic('');
          // Actualizar las fuentes
          fetchAllSources();
        }, 2000);
      }
    } catch (err) {
      console.error('Error loading to XIA:', err);
      setScrapingStatus('found');
    }
  };

  const toggleUrlSelection = (url) => {
    setScrapingSelected(prev =>
      prev.includes(url)
        ? prev.filter(u => u !== url)
        : [...prev, url]
    );
  };

  const selectAllUrls = () => {
    setScrapingSelected(scrapingUrls.map(u => u.url));
  };

  const deselectAllUrls = () => {
    setScrapingSelected([]);
  };

  // Auto-scraping: buscar e inyectar en un solo paso
  const handleAutoScraping = async () => {
    if (!scrapingTopic.trim()) return;

    setScrapingStatus('loading');
    setScrapingProgress({ current: 0, total: 1 });

    try {
      console.log('🚀 Auto-scraping iniciado para:', scrapingTopic);
      const response = await fetch(`${API_URL}/api/xia-training/scraping/auto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: scrapingTopic,
          department: scrapingDepartment,
          maxUrls: 10
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Auto-scraping completado:', data);

        // Actualizar progreso
        setScrapingProgress({
          current: data.urlsProcessed || 0,
          total: data.urlsFound || 1
        });

        // Mostrar resultados
        if (data.results) {
          setScrapingUrls(data.results.map(r => ({
            url: r.url,
            source: r.source || 'Auto',
            status: r.status
          })));
        }

        // Limpiar después de 3 segundos
        setTimeout(() => {
          setScrapingStatus('idle');
          setScrapingTopic('');
          setScrapingUrls([]);
          fetchAllSources();
        }, 3000);
      } else {
        console.error('❌ Error en auto-scraping');
        setScrapingStatus('idle');
      }
    } catch (err) {
      console.error('💥 Error en auto-scraping:', err);
      setScrapingStatus('idle');
    }
  };

  // ========== FUNCIONES DE DIAGNÓSTICOS ==========
  const fetchDiagnostics = async () => {
    setDiagnosticsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/xia-training/diagnostics`);
      if (response.ok) {
        const data = await response.json();
        setDiagnostics(data.diagnostics || []);
      }
    } catch (err) {
      console.error('Error fetching diagnostics:', err);
    }
    setDiagnosticsLoading(false);
  };

  const saveDiagnostic = async (diagnostic) => {
    try {
      const response = await fetch(`${API_URL}/api/xia-training/diagnostics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(diagnostic)
      });
      if (response.ok) {
        const data = await response.json();
        fetchDiagnostics(); // Recargar lista
        setEditingDiagnostic(null);
        resetNewDiagnostic();
        return data;
      }
    } catch (err) {
      console.error('Error saving diagnostic:', err);
    }
    return null;
  };

  const deleteDiagnostic = async (id) => {
    if (!window.confirm('¿Eliminar este diagnóstico?')) return;
    try {
      const response = await fetch(`${API_URL}/api/xia-training/diagnostics/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        fetchDiagnostics();
      }
    } catch (err) {
      console.error('Error deleting diagnostic:', err);
    }
  };

  const resetNewDiagnostic = () => {
    setNewDiagnostic({
      nombre: '',
      keywords: [],
      pasos: {
        inicio: {
          pregunta: '',
          opciones: [
            { texto: 'Sí', siguiente: '' },
            { texto: 'No', siguiente: '' },
            { texto: 'No lo sé', siguiente: '' }
          ]
        }
      }
    });
    setSelectedStep('inicio');
    setNewKeyword('');
  };

  const addStepToDiagnostic = (stepId) => {
    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    if (diagnostic.pasos[stepId]) return; // Ya existe

    setDiagnostic({
      ...diagnostic,
      pasos: {
        ...diagnostic.pasos,
        [stepId]: {
          pregunta: '',
          opciones: [
            { texto: 'Sí', siguiente: '' },
            { texto: 'No', siguiente: '' },
            { texto: 'No lo sé', siguiente: '' }
          ]
        }
      }
    });
    setSelectedStep(stepId);
  };

  const updateStepQuestion = (stepId, pregunta) => {
    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    setDiagnostic({
      ...diagnostic,
      pasos: {
        ...diagnostic.pasos,
        [stepId]: {
          ...diagnostic.pasos[stepId],
          pregunta
        }
      }
    });
  };

  const updateStepOption = (stepId, optionIndex, field, value) => {
    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    const newOpciones = [...diagnostic.pasos[stepId].opciones];
    newOpciones[optionIndex] = { ...newOpciones[optionIndex], [field]: value };

    setDiagnostic({
      ...diagnostic,
      pasos: {
        ...diagnostic.pasos,
        [stepId]: {
          ...diagnostic.pasos[stepId],
          opciones: newOpciones
        }
      }
    });
  };

  const addOptionToStep = (stepId) => {
    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    setDiagnostic({
      ...diagnostic,
      pasos: {
        ...diagnostic.pasos,
        [stepId]: {
          ...diagnostic.pasos[stepId],
          opciones: [...diagnostic.pasos[stepId].opciones, { texto: '', siguiente: '' }]
        }
      }
    });
  };

  const removeOptionFromStep = (stepId, optionIndex) => {
    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    const newOpciones = diagnostic.pasos[stepId].opciones.filter((_, i) => i !== optionIndex);

    setDiagnostic({
      ...diagnostic,
      pasos: {
        ...diagnostic.pasos,
        [stepId]: {
          ...diagnostic.pasos[stepId],
          opciones: newOpciones
        }
      }
    });
  };

  const deleteDiagnosticStep = (stepId) => {
    if (stepId === 'inicio') return; // No se puede eliminar el paso inicial

    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    const newPasos = { ...diagnostic.pasos };
    delete newPasos[stepId];

    setDiagnostic({
      ...diagnostic,
      pasos: newPasos
    });

    if (selectedStep === stepId) {
      setSelectedStep('inicio');
    }
  };

  const addKeyword = () => {
    if (!newKeyword.trim()) return;
    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    if (!diagnostic.keywords.includes(newKeyword.trim().toLowerCase())) {
      setDiagnostic({
        ...diagnostic,
        keywords: [...diagnostic.keywords, newKeyword.trim().toLowerCase()]
      });
    }
    setNewKeyword('');
  };

  const removeKeyword = (keyword) => {
    const diagnostic = editingDiagnostic || newDiagnostic;
    const setDiagnostic = editingDiagnostic ? setEditingDiagnostic : setNewDiagnostic;

    setDiagnostic({
      ...diagnostic,
      keywords: diagnostic.keywords.filter(k => k !== keyword)
    });
  };

  // Cargar diagnósticos cuando se cambia a la pestaña
  useEffect(() => {
    if (activeTab === 'diagnosticos' && isAuthenticated) {
      fetchDiagnostics();
    }
  }, [activeTab, isAuthenticated]);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(filters.department !== 'all' && { department: filters.department }),
        ...(filters.learned !== 'all' && { learned: filters.learned }),
        ...(filters.dateFrom && { dateFrom: filters.dateFrom }),
        ...(filters.dateTo && { dateTo: filters.dateTo })
      });

      const response = await fetch(`${API_URL}/api/xia-training/records?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || 'xia-standalone'}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLogs(data.records || []);
        setTotalPages(data.totalPages || 1);
      } else {
        setLogs(getMockLogs());
        setTotalPages(3);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setLogs(getMockLogs());
      setTotalPages(3);
    }
    setLogsLoading(false);
  };

  // TIPOS de conocimiento
  const KNOWLEDGE_TYPES = [
    { id: 'conocimiento', name: 'Conocimiento', icon: '📚', color: 'blue' },
    { id: 'logica', name: 'Lógica', icon: '🧠', color: 'purple' },
  ];

  // Sin datos mock - datos reales vendrán del backend
  const getMockLogs = () => [];

  // Generar preguntas mock
  function generateMockQuestions(topic, count) {
    const questionTypes = [
      `¿Qué es ${topic}?`,
      `Explica los pasos principales de ${topic}`,
      `¿Cuáles son los requisitos para ${topic}?`,
      `¿Qué errores comunes hay en ${topic}?`,
      `¿Cómo se aplica ${topic} en la práctica?`,
      `Define los conceptos clave de ${topic}`,
      `¿Qué normativa aplica a ${topic}?`,
      `¿Cuáles son las mejores prácticas en ${topic}?`,
      `¿Cómo se resuelven problemas en ${topic}?`,
      `Resume brevemente ${topic}`,
    ];

    const questions = [];
    for (let i = 0; i < count; i++) {
      const baseQ = questionTypes[i % questionTypes.length];
      questions.push({
        id: i + 1,
        question: i < 10 ? baseQ : `Pregunta ${i + 1}: ${baseQ.replace(topic, `aspecto ${i + 1} de ${topic}`)}`,
        answered: Math.random() > 0.2,
        correct: Math.random() > 0.3,
        date: new Date(Date.now() - Math.random() * 86400000).toISOString()
      });
    }
    return questions;
  }

  const handleLogin = (e) => {
    e.preventDefault();
    setError('');

    const user = username.trim().toLowerCase();
    const pass = password.trim();

    if (!user || !pass) {
      setError('Por favor, introduce usuario y contraseña');
      return;
    }

    if (XIA_USERS[user] && XIA_USERS[user].password === pass) {
      const auth = {
        user,
        role: XIA_USERS[user].role,
        expires: Date.now() + (24 * 60 * 60 * 1000)
      };
      localStorage.setItem('xia_auth', JSON.stringify(auth));
      setIsAuthenticated(true);
      setCurrentUser(auth);
    } else {
      setError('Usuario o contraseña incorrectos');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('xia_auth');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setUsername('');
    setPassword('');
  };

  // Acciones de logs
  const handlePauseToggle = async (log) => {
    setLogs(logs.map(l => l.id === log.id ? { ...l, paused: !l.paused } : l));
  };

  const handleDelete = async () => {
    if (deleteStep === 0) {
      setDeleteStep(1);
      return;
    }
    setLogs(logs.filter(l => l.id !== deleteConfirm.id));
    setDeleteConfirm(null);
    setDeleteStep(0);
  };

  const handleSaveEdit = async () => {
    setLogs(logs.map(l => l.id === editingLog.id ? editingLog : l));
    setEditingLog(null);
  };

  // Subida de archivos con cola por prioridad - REAL
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Añadir a cola de subida con prioridad
    const newFilesQueue = files.map((file, i) => ({
      id: Date.now() + i,
      name: file.name,
      type: file.type,
      size: file.size,
      file: file, // Archivo real para subir
      department: selectedDepartment,
      tipo: selectedTipo,
      priority: selectedPriority,
      procedencia: file.name,
      status: 'queued',
      progress: 0,
      date: new Date().toISOString()
    }));

    // Añadir a cola y ordenar por prioridad (mayor primero)
    setUploadingFiles(prev => [...prev, ...newFilesQueue].sort((a, b) => b.priority - a.priority));
    setUploadProgress({ current: 0, total: files.length, status: 'uploading' });

    // Procesar cola (en orden de prioridad)
    for (let i = 0; i < newFilesQueue.length; i++) {
      const fileData = newFilesQueue[i];

      try {
        // Actualizar progreso - subiendo
        setUploadingFiles(prev => prev.map(f => f.id === fileData.id ? { ...f, progress: 20, status: 'uploading' } : f));

        // Crear FormData para subir archivo real
        const formData = new FormData();
        formData.append('file', fileData.file);
        formData.append('department', fileData.department);
        formData.append('tipo', fileData.tipo);
        formData.append('priority', fileData.priority);
        formData.append('procedencia', fileData.procedencia);

        // Subir al backend
        setUploadingFiles(prev => prev.map(f => f.id === fileData.id ? { ...f, progress: 50, status: 'processing' } : f));

        const response = await fetch(`${API_URL}/api/xia-training/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token') || 'xia-standalone'}`
          },
          body: formData
        });

        setUploadingFiles(prev => prev.map(f => f.id === fileData.id ? { ...f, progress: 80 } : f));

        let result = { success: false, questionsGenerated: 0 };
        if (response.ok) {
          result = await response.json();
        }

        // Mover a subidos
        setUploadingFiles(prev => prev.filter(f => f.id !== fileData.id));
        setUploadedFiles(prev => [...prev, {
          ...fileData,
          file: undefined, // No guardar el archivo en memoria
          status: result.success ? 'processed' : 'error',
          questionsGenerated: result.questionsGenerated || 0,
          error: result.error
        }]);

        if (result.success) {
          setUploadStats(prev => ({
            ...prev,
            totalUploaded: prev.totalUploaded + 1,
            totalInjected: prev.totalInjected + (result.injected ? 1 : 0)
          }));
        }

      } catch (err) {
        console.error('Error uploading file:', err);
        // Mover a subidos con error
        setUploadingFiles(prev => prev.filter(f => f.id !== fileData.id));
        setUploadedFiles(prev => [...prev, {
          ...fileData,
          file: undefined,
          status: 'error',
          questionsGenerated: 0,
          error: err.message
        }]);
      }

      setUploadProgress({ current: i + 1, total: files.length, status: 'uploading', file: fileData.name });
    }

    setUploadProgress({ current: files.length, total: files.length, status: 'complete' });
    setTimeout(() => setUploadProgress(null), 2000);
  };

  const handleDriveImport = async () => {
    if (!driveUrl.trim()) return;

    const fileData = {
      id: Date.now(),
      name: driveUrl.includes('folder') ? 'Carpeta de Drive' : 'Archivo de Drive',
      type: 'drive',
      department: selectedDepartment,
      tipo: selectedTipo,
      priority: selectedPriority,
      procedencia: driveUrl,
      status: 'queued',
      progress: 0,
      date: new Date().toISOString()
    };

    // Añadir a cola visual
    setUploadingFiles(prev => [...prev, fileData].sort((a, b) => b.priority - a.priority));
    setUploadProgress({ current: 0, total: 1, status: 'importing', file: driveUrl });

    try {
      // Llamar a la API de Drive
      const response = await fetch(`${API_URL}/api/xia-training/drive/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          folderUrl: driveUrl,
          department: selectedDepartment,
          addUrlsToQueue: true
        })
      });

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Error procesando Drive');
      }

      // Actualizar progreso - el procesamiento es en background
      setUploadingFiles(prev => prev.map(f => f.id === fileData.id ? { ...f, progress: 50, status: 'processing' } : f));

      // Polling para ver el progreso
      const checkProgress = async () => {
        const statusRes = await fetch(`${API_URL}/api/xia-training/drive/status`);
        const status = await statusRes.json();

        if (status.active) {
          const progress = status.progress;
          if (progress.stage === 'processing') {
            const pct = Math.round((progress.current / progress.total) * 100);
            setUploadingFiles(prev => prev.map(f => f.id === fileData.id ? { ...f, progress: pct } : f));
          }
          setTimeout(checkProgress, 2000);
        } else {
          // Completado
          const results = status.results || {};
          setUploadingFiles(prev => prev.filter(f => f.id !== fileData.id));
          setUploadedFiles(prev => [...prev, {
            ...fileData,
            status: results.error ? 'error' : 'processed',
            error: results.error,
            questionsGenerated: results.documents?.length || 0,
            urlsFound: results.urls?.length || 0,
            threats: results.threats?.length || 0
          }]);

          setUploadStats(prev => ({ ...prev, totalUploaded: prev.totalUploaded + 1 }));
          setUploadProgress({ current: 1, total: 1, status: 'complete' });
          setTimeout(() => setUploadProgress(null), 2000);
        }
      };

      setTimeout(checkProgress, 3000);

    } catch (err) {
      console.error('Error importing from Drive:', err);
      setUploadingFiles(prev => prev.filter(f => f.id !== fileData.id));
      setUploadedFiles(prev => [...prev, {
        ...fileData,
        status: 'error',
        error: err.message
      }]);
      setUploadProgress(null);
    }

    setDriveUrl('');
  };

  const getDepartmentInfo = (deptId) => {
    return DEPARTMENTS.find(d => d.id === deptId) || { name: deptId, icon: '📁' };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFileIcon = (type) => {
    if (type?.includes('pdf')) return <FileText className="w-5 h-5 text-red-500" />;
    if (type?.includes('word') || type?.includes('document')) return <FileText className="w-5 h-5 text-blue-500" />;
    if (type?.includes('sheet') || type?.includes('excel')) return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    if (type?.includes('image')) return <Image className="w-5 h-5 text-purple-500" />;
    if (type === 'drive') return <FolderOpen className="w-5 h-5 text-yellow-500" />;
    return <File className="w-5 h-5 text-gray-500" />;
  };

  // Pantalla de carga
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-center">
          <img src="/xia-logo.png" alt="XIA" className="w-20 h-20 mx-auto animate-pulse" />
          <p className="mt-4 text-white/80">Cargando XIA...</p>
        </div>
      </div>
    );
  }

  // Si está autenticado
  if (isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col">
        {/* Header - Responsive */}
        <div className="bg-white border-b border-gray-200 px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Logo y título */}
            <div className="flex items-center gap-2">
              <img src="/xia-icon.png" alt="XIA" className="w-10 h-10 object-contain" />
              <span className="font-semibold text-gray-800 hidden sm:inline">XIA - Asistente Inteligente</span>
              <span className="font-semibold text-gray-800 sm:hidden">XIA</span>
            </div>

            {/* Tabs - Desktop */}
            <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('chat')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  activeTab === 'chat' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <MessageSquare className="w-4 h-4" />
                Chat
              </button>
              {currentUser?.role === 'admin' && (
                <>
                  <button
                    onClick={() => setActiveTab('datos')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === 'datos' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Upload className="w-4 h-4" />
                    Datos
                  </button>
                  <button
                    onClick={() => setActiveTab('script')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === 'script' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <CircleDot className={`w-4 h-4 ${injectionStatus === 'running' ? 'text-green-500 animate-pulse' : ''}`} />
                    Script
                    {injectionStatus === 'running' && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
                  </button>
                  <button
                    onClick={() => setActiveTab('scraping')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === 'scraping' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Globe className="w-4 h-4" />
                    Scraping
                  </button>
                  <button
                    onClick={() => setActiveTab('diagnosticos')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === 'diagnosticos' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Wrench className="w-4 h-4" />
                    Diagnosticos
                  </button>
                  <button
                    onClick={() => setActiveTab('logs')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                      activeTab === 'logs' ? 'bg-white text-blue-600 shadow' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Database className="w-4 h-4" />
                    Logs
                  </button>
                </>
              )}
            </div>

            {/* Botones derecha */}
            <div className="flex items-center gap-2">
              {/* Botón salir - Desktop */}
              <button
                onClick={handleLogout}
                className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
              >
                <LogOut className="w-4 h-4" />
                Salir
              </button>

              {/* Menú hamburguesa - Mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {mobileMenuOpen ? <CloseIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Menú móvil desplegable */}
          {mobileMenuOpen && (
            <div className="md:hidden mt-3 pb-3 border-t border-gray-100 pt-3">
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => { setActiveTab('chat'); setMobileMenuOpen(false); }}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition ${
                    activeTab === 'chat' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <MessageSquare className="w-5 h-5" />
                  Chat con XIA
                </button>
                {currentUser?.role === 'admin' && (
                  <>
                    <button
                      onClick={() => { setActiveTab('datos'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition ${
                        activeTab === 'datos' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Upload className="w-5 h-5" />
                      Subir Datos
                    </button>
                    <button
                      onClick={() => { setActiveTab('script'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition ${
                        activeTab === 'script' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <CircleDot className={`w-5 h-5 ${injectionStatus === 'running' ? 'text-green-500' : ''}`} />
                      Script de Inyección
                      {injectionStatus === 'running' && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
                    </button>
                    <button
                      onClick={() => { setActiveTab('scraping'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition ${
                        activeTab === 'scraping' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Globe className="w-5 h-5" />
                      Scraping Fuentes
                    </button>
                    <button
                      onClick={() => { setActiveTab('diagnosticos'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition ${
                        activeTab === 'diagnosticos' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Wrench className="w-5 h-5" />
                      Diagnosticos SAT
                    </button>
                    <button
                      onClick={() => { setActiveTab('logs'); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium transition ${
                        activeTab === 'logs' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      <Database className="w-5 h-5" />
                      Logs de Formación
                    </button>
                  </>
                )}
                <hr className="my-2 border-gray-200" />
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg text-left font-medium text-red-600 hover:bg-red-50 transition"
                >
                  <LogOut className="w-5 h-5" />
                  Cerrar Sesión
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        {activeTab === 'chat' ? (
          <div className="flex-1 p-4">
            <div className="max-w-4xl mx-auto h-[calc(100vh-120px)]">
              <AIChat user={{ full_name: currentUser?.user || 'Usuario XIA' }} />
            </div>
          </div>
        ) : activeTab === 'script' ? (
          /* ========== PESTAÑA SCRIPT ========== */
          <div className="flex-1 p-4 overflow-auto">
            <div className="max-w-4xl mx-auto space-y-6">

              {/* 1. AÑADIR URL - Primero el formulario */}
              <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-blue-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Link className="w-5 h-5 text-blue-600" />
                  1. Añadir URL para Aprender
                </h3>

                <div className="space-y-4">
                  {/* URL Input */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      URL de la página web
                    </label>
                    <input
                      type="url"
                      value={newUrl}
                      onChange={(e) => setNewUrl(e.target.value)}
                      placeholder="https://ejemplo.com/articulo"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Departamento y Procedencia en línea */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Departamento
                      </label>
                      <select
                        value={newUrlDepartment}
                        onChange={(e) => setNewUrlDepartment(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {DEPARTMENTS.map(dept => (
                          <option key={dept.id} value={dept.id}>
                            {dept.icon} {dept.name}
                          </option>
                        ))}
                        <option value="general">🌐 GENERAL</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Procedencia
                      </label>
                      <div className="px-3 py-2 bg-purple-50 border-2 border-purple-300 rounded-lg">
                        <span className="text-sm font-medium text-purple-700">👤 REVISOR (verificado)</span>
                      </div>
                    </div>
                  </div>

                  {/* Modo Crawler */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={crawlMode}
                        onChange={(e) => setCrawlMode(e.target.checked)}
                        className="w-5 h-5 mt-0.5 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <div>
                        <span className="font-semibold text-gray-900 flex items-center gap-2">
                          🕷️ Modo Crawler Completo
                        </span>
                        <p className="text-xs text-gray-600 mt-1">
                          Sigue todos los enlaces, descarga PDFs, analiza vídeos de YouTube
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Auth toggle */}
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => setShowUrlAuth(!showUrlAuth)}
                      className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <Lock className="w-4 h-4" />
                      {showUrlAuth ? 'Ocultar credenciales' : '¿Necesita usuario/contraseña?'}
                    </button>
                  </div>

                  {showUrlAuth && (
                    <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
                      <input
                        type="text"
                        value={newUrlAuth.user}
                        onChange={(e) => setNewUrlAuth(prev => ({ ...prev, user: e.target.value }))}
                        placeholder="Usuario"
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      />
                      <input
                        type="password"
                        value={newUrlAuth.pass}
                        onChange={(e) => setNewUrlAuth(prev => ({ ...prev, pass: e.target.value }))}
                        placeholder="Contraseña"
                        className="px-3 py-2 border border-gray-300 rounded-lg"
                      />
                    </div>
                  )}

                  {/* Botón añadir */}
                  <button
                    onClick={handleAddUrl}
                    disabled={!newUrl.trim() || addingUrl}
                    className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition ${
                      !newUrl.trim() || addingUrl
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {addingUrl ? (
                      <>
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Añadiendo...
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5" />
                        Añadir a la Cola
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* 2. CONTROL DEL SCRIPT - Después de añadir */}
              <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-green-200">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <CircleDot className="w-5 h-5 text-green-600" />
                  2. Procesar Cola de URLs
                </h2>

                {/* Estado actual y tiempo */}
                <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
                  <div className={`px-6 py-3 rounded-xl text-center ${
                    injectionStatus === 'running' ? 'bg-green-100 border-2 border-green-500' :
                    injectionStatus === 'paused' ? 'bg-yellow-100 border-2 border-yellow-500' :
                    'bg-gray-100 border-2 border-gray-300'
                  }`}>
                    <p className="text-sm text-gray-600">Estado actual</p>
                    <p className={`text-xl font-bold ${
                      injectionStatus === 'running' ? 'text-green-700' :
                      injectionStatus === 'paused' ? 'text-yellow-700' :
                      'text-gray-700'
                    }`}>
                      {injectionStatus === 'running' ? '▶ EJECUTANDO' :
                       injectionStatus === 'paused' ? '⏸ PAUSADO' :
                       '⏹ DETENIDO'}
                    </p>
                  </div>

                  {/* Tiempo transcurrido */}
                  {(injectionStatus === 'running' || injectionStatus === 'paused') && (
                    <div className="px-6 py-3 rounded-xl text-center bg-blue-100 border-2 border-blue-500">
                      <p className="text-sm text-gray-600">Tiempo ejecutando</p>
                      <p className="text-xl font-bold text-blue-700 font-mono">
                        {formatElapsedTime(elapsedTime)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Botones de control */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => handleScriptControl('play')}
                    disabled={injectionLoading || injectionStatus === 'running'}
                    className={`flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition ${
                      injectionStatus === 'running'
                        ? 'bg-green-200 text-green-800 cursor-not-allowed'
                        : 'bg-green-500 hover:bg-green-600 text-white shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <Play className="w-6 h-6" />
                    PLAY
                  </button>

                  <button
                    onClick={() => handleScriptControl('pause')}
                    disabled={injectionLoading || injectionStatus !== 'running'}
                    className={`flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition ${
                      injectionStatus !== 'running'
                        ? 'bg-yellow-200 text-yellow-800 cursor-not-allowed'
                        : 'bg-yellow-500 hover:bg-yellow-600 text-white shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <Pause className="w-6 h-6" />
                    PAUSA
                  </button>

                  <button
                    onClick={() => handleScriptControl('stop')}
                    disabled={injectionLoading || injectionStatus === 'stopped'}
                    className={`flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-lg transition ${
                      injectionStatus === 'stopped'
                        ? 'bg-red-200 text-red-800 cursor-not-allowed'
                        : 'bg-red-500 hover:bg-red-600 text-white shadow-lg hover:shadow-xl'
                    }`}
                  >
                    <Square className="w-6 h-6" />
                    STOP
                  </button>
                </div>

                {injectionLoading && (
                  <div className="flex items-center justify-center mt-4">
                    <RefreshCw className="w-5 h-5 animate-spin text-blue-500 mr-2" />
                    <span className="text-gray-600">Procesando...</span>
                  </div>
                )}
              </div>

              {/* Estadísticas rápidas */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-blue-700">{uploadStats.totalUploaded}</p>
                  <p className="text-sm text-blue-600">Total Fuentes</p>
                </div>
                <div className="bg-green-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-700">{uploadStats.totalInjected}</p>
                  <p className="text-sm text-green-600">Inyectados</p>
                </div>
                <div className="bg-orange-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-orange-700">{uploadStats.totalPending}</p>
                  <p className="text-sm text-orange-600">Pendientes</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-purple-700">{uploadingFiles.length}</p>
                  <p className="text-sm text-purple-600">En Cola</p>
                </div>
              </div>

              {/* Listado de Fuentes */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Todas las Fuentes ({uploadedFiles.length})
                  </h3>
                  <button
                    onClick={fetchAllSources}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>

                <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                  {uploadedFiles.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No hay fuentes cargadas</p>
                      <p className="text-sm">Sube archivos desde la pestaña "Datos"</p>
                    </div>
                  ) : (
                    uploadedFiles.map((file, idx) => {
                      const dept = getDepartmentInfo(file.department);
                      return (
                        <div key={file.id || idx} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded font-bold text-xs ${
                              file.priority >= 8 ? 'bg-red-100 text-red-700' :
                              file.priority >= 5 ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              P{file.priority || 5}
                            </span>
                            {getFileIcon(file.type)}
                            <div>
                              <p className="font-medium text-gray-900">{file.name || file.topic}</p>
                              <p className="text-sm text-gray-500">
                                {dept.icon} {dept.name} •
                                <span className={`ml-1 ${file.tipo === 'logica' ? 'text-purple-600' : 'text-blue-600'}`}>
                                  {file.tipo === 'logica' ? '🧠' : '📚'}
                                </span>
                                • {file.questionsCount || file.questionsGenerated || 0} preguntas
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {file.learned ? (
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">✓ Aprendido</span>
                            ) : file.status === 'processing' ? (
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1">
                                <RefreshCw className="w-3 h-3 animate-spin" /> Procesando
                              </span>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">Pendiente</span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Log del Script */}
              {scriptLogs.length > 0 && (
                <div className="bg-gray-900 rounded-xl p-4 text-green-400 font-mono text-sm max-h-[200px] overflow-y-auto">
                  <p className="text-gray-500 mb-2"># Log del script</p>
                  {scriptLogs.map((log, idx) => (
                    <div key={idx} className="flex gap-2">
                      <span className="text-gray-500">[{log.time}]</span>
                      <span className={
                        log.action === 'PLAY' ? 'text-green-400' :
                        log.action === 'PAUSE' ? 'text-yellow-400' :
                        log.action === 'STOP' ? 'text-red-400' :
                        'text-gray-400'
                      }>{log.action}:</span>
                      <span>{log.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Agentes/Conocimiento Cargado */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <Database className="w-5 h-5 text-purple-600" />
                    Agentes Cargados (Conocimiento XIA)
                  </h3>
                  <button
                    onClick={fetchFaqStats}
                    disabled={agentsLoading}
                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                  >
                    <RefreshCw className={`w-4 h-4 ${agentsLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>

                {/* Estadísticas rápidas */}
                <div className="grid grid-cols-3 gap-2 p-4 bg-gray-50">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-700">{faqStats.totalEnCache || 0}</p>
                    <p className="text-xs text-gray-600">Total Q&A</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-700">{faqStats.preguntasAsimiladas || 0}</p>
                    <p className="text-xs text-gray-600">Asimiladas</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-700">{loadedAgents.length}</p>
                    <p className="text-xs text-gray-600">Fuentes</p>
                  </div>
                </div>

                {/* Por departamento */}
                {faqStats.porDepartamento && Object.keys(faqStats.porDepartamento).length > 0 && (
                  <div className="p-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-700 mb-2">Por departamento:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(faqStats.porDepartamento).map(([dept, count]) => {
                        const deptInfo = DEPARTMENTS.find(d => d.id === dept) || { icon: '🌐', name: dept };
                        return (
                          <span key={dept} className="px-3 py-1 bg-gray-100 rounded-full text-sm">
                            {deptInfo.icon} {deptInfo.name}: <strong>{count}</strong>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Lista de agentes/fuentes cargadas */}
                <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                  {loadedAgents.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="font-medium">Sin agentes cargados</p>
                      <p className="text-sm">Añade URLs arriba para que XIA aprenda</p>
                    </div>
                  ) : (
                    loadedAgents.map((agent, idx) => {
                      const deptInfo = DEPARTMENTS.find(d => d.id === agent.department) || { icon: '🌐', name: agent.department };
                      const isReviewer = agent.source_type === 'reviewer' || agent.assimilatedBy?.includes('Revisor');
                      return (
                        <div key={agent.id || idx} className="p-3 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-2 flex-1">
                              {/* Indicador Revisor/IA */}
                              <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                                isReviewer ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {isReviewer ? '👤' : '🤖'}
                              </span>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900 text-sm">{agent.topic}</p>
                                <p className="text-xs text-gray-500 mt-1 truncate">
                                  {deptInfo.icon} {deptInfo.name} •
                                  <span className="text-blue-600 ml-1">{agent.procedencia?.substring(0, 35)}...</span>
                                </p>
                              </div>
                            </div>
                            <span className={`px-2 py-0.5 rounded text-xs ${
                              agent.learned ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {agent.learned ? '✓ Activo' : 'Pendiente'}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === 'scraping' ? (
          /* ========== PESTAÑA SCRAPING ========== */
          <div className="flex-1 p-4 overflow-auto">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Panel de Búsqueda */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Globe className="w-5 h-5 text-blue-600" />
                  Scraping de Fuentes Fidedignas
                </h2>

                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <BookOpen className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-blue-900">Solo fuentes verificadas</p>
                      <p className="text-xs text-blue-700 mt-1">
                        Universidades (UPM, MIT, Stanford...), documentación oficial (Microsoft, Mozilla, AWS),
                        organizaciones estándar (ISO, IEEE, OWASP) y fuentes del estado (INCIBE, CISA, NIST).
                      </p>
                    </div>
                  </div>
                </div>

                {/* Selector departamento */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Departamento destino</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {DEPARTMENTS.map(dept => (
                      <button
                        key={dept.id}
                        onClick={() => setScrapingDepartment(dept.id)}
                        className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg border-2 transition text-sm ${
                          scrapingDepartment === dept.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span>{dept.icon}</span>
                        <span className="font-medium truncate">{dept.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Campo temática */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Temática a buscar
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={scrapingTopic}
                      onChange={(e) => setScrapingTopic(e.target.value)}
                      placeholder="Ej: gestión de incidencias ITIL, soporte técnico, redes..."
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={scrapingStatus === 'searching' || scrapingStatus === 'loading'}
                    />
                    <button
                      onClick={handleSearchScraping}
                      disabled={!scrapingTopic.trim() || scrapingStatus === 'searching' || scrapingStatus === 'loading'}
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition ${
                        scrapingStatus === 'searching'
                          ? 'bg-blue-200 text-blue-700'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {scrapingStatus === 'searching' ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Buscando...
                        </>
                      ) : (
                        <>
                          <Search className="w-5 h-5" />
                          Buscar
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleAutoScraping}
                      disabled={!scrapingTopic.trim() || scrapingStatus === 'searching' || scrapingStatus === 'loading'}
                      className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition ${
                        scrapingStatus === 'loading'
                          ? 'bg-green-200 text-green-700'
                          : 'bg-green-500 hover:bg-green-600 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                      title="Busca e inyecta automáticamente en XIA"
                    >
                      {scrapingStatus === 'loading' ? (
                        <>
                          <Loader className="w-5 h-5 animate-spin" />
                          Inyectando...
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5" />
                          Auto-Inyectar
                        </>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    <strong>Buscar:</strong> Ver URLs primero | <strong>Auto-Inyectar:</strong> Buscar e inyectar directamente a XIA
                  </p>
                </div>

                {/* Sugerencias rápidas */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="text-xs text-gray-500">Sugerencias:</span>
                  {['soporte_tecnico', 'itil', 'programacion', 'seguridad', 'cloud'].map(topic => (
                    <button
                      key={topic}
                      onClick={() => setScrapingTopic(topic)}
                      className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-xs transition"
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Resultados de búsqueda */}
              {scrapingStatus === 'found' && scrapingUrls.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      URLs Encontradas ({scrapingUrls.length})
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={selectAllUrls}
                        className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Seleccionar todo
                      </button>
                      <button
                        onClick={deselectAllUrls}
                        className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                      >
                        Deseleccionar
                      </button>
                    </div>
                  </div>

                  <div className="divide-y divide-gray-100 max-h-[300px] overflow-y-auto">
                    {scrapingUrls.map((item, idx) => (
                      <div key={idx} className="p-3 hover:bg-gray-50 flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={scrapingSelected.includes(item.url)}
                          onChange={() => toggleUrlSelection(item.url)}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <Link className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{item.url}</p>
                          {item.source && (
                            <p className="text-xs text-gray-500">{item.source}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">
                        {scrapingSelected.length} de {scrapingUrls.length} seleccionadas
                      </span>
                      <button
                        onClick={handleLoadToXIA}
                        disabled={scrapingSelected.length === 0}
                        className="flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Play className="w-5 h-5" />
                        Cargar a XIA ({scrapingSelected.length})
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Estado de carga */}
              {scrapingStatus === 'loading' && (
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="text-center">
                    <Loader className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Cargando a XIA...</h3>
                    <p className="text-gray-600 mb-4">
                      Procesando {scrapingProgress.current} de {scrapingProgress.total} URLs
                    </p>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 transition-all duration-300"
                        style={{ width: `${(scrapingProgress.current / scrapingProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Sin resultados */}
              {scrapingStatus === 'found' && scrapingUrls.length === 0 && (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No se encontraron URLs para esta temática</p>
                  <p className="text-sm text-gray-500 mt-1">Prueba con otro término de búsqueda</p>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'diagnosticos' ? (
          /* ========== PESTAÑA DIAGNÓSTICOS ========== */
          <div className="flex-1 p-4 overflow-auto">
            <div className="max-w-5xl mx-auto space-y-6">
              {/* Header */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Wrench className="w-6 h-6 text-blue-500" />
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Diagnósticos Guiados SAT</h2>
                      <p className="text-sm text-gray-500">Crea flujos de diagnóstico paso a paso para el servicio técnico</p>
                    </div>
                  </div>
                  {!editingDiagnostic && (
                    <button
                      onClick={() => {
                        resetNewDiagnostic();
                        setEditingDiagnostic({ isNew: true });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                    >
                      <Plus className="w-4 h-4" />
                      Nuevo Diagnóstico
                    </button>
                  )}
                </div>

                {/* Lista de diagnósticos o Editor */}
                {diagnosticsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-blue-500" />
                  </div>
                ) : editingDiagnostic ? (
                  /* ========== EDITOR DE DIAGNÓSTICO ========== */
                  <div className="space-y-6">
                    {/* Nombre y keywords */}
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del equipo/problema</label>
                        <input
                          type="text"
                          value={editingDiagnostic.isNew ? newDiagnostic.nombre : editingDiagnostic.nombre}
                          onChange={(e) => {
                            if (editingDiagnostic.isNew) {
                              setNewDiagnostic({ ...newDiagnostic, nombre: e.target.value });
                            } else {
                              setEditingDiagnostic({ ...editingDiagnostic, nombre: e.target.value });
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Ej: Torno, Kiosco, Impresora..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Palabras clave (para detectar)</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            placeholder="Añadir palabra clave..."
                          />
                          <button onClick={addKeyword} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg">
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {(editingDiagnostic.isNew ? newDiagnostic.keywords : editingDiagnostic.keywords).map((kw, i) => (
                            <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm">
                              {kw}
                              <button onClick={() => removeKeyword(kw)} className="hover:text-red-600">
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Editor de pasos */}
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                        <h3 className="font-medium text-gray-900">Pasos del diagnóstico</h3>
                        <button
                          onClick={() => {
                            const newStepId = `paso_${Object.keys(editingDiagnostic.isNew ? newDiagnostic.pasos : editingDiagnostic.pasos).length + 1}`;
                            addStepToDiagnostic(newStepId);
                          }}
                          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
                        >
                          <Plus className="w-4 h-4" />
                          Añadir paso
                        </button>
                      </div>

                      <div className="flex">
                        {/* Lista de pasos */}
                        <div className="w-48 border-r border-gray-200 bg-gray-50">
                          {Object.keys(editingDiagnostic.isNew ? newDiagnostic.pasos : editingDiagnostic.pasos).map((stepId) => (
                            <button
                              key={stepId}
                              onClick={() => setSelectedStep(stepId)}
                              className={`w-full px-4 py-3 text-left text-sm transition flex items-center justify-between ${
                                selectedStep === stepId
                                  ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-500'
                                  : 'hover:bg-gray-100 text-gray-600'
                              }`}
                            >
                              <span className="truncate">{stepId === 'inicio' ? 'Inicio' : stepId.replace('_', ' ')}</span>
                              {stepId !== 'inicio' && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteDiagnosticStep(stepId); }}
                                  className="text-red-400 hover:text-red-600"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              )}
                            </button>
                          ))}
                        </div>

                        {/* Editor del paso seleccionado */}
                        <div className="flex-1 p-4 space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Pregunta del paso</label>
                            <textarea
                              value={(editingDiagnostic.isNew ? newDiagnostic.pasos : editingDiagnostic.pasos)[selectedStep]?.pregunta || ''}
                              onChange={(e) => updateStepQuestion(selectedStep, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                              placeholder="Ej: ¿El equipo tiene corriente?"
                            />
                          </div>

                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <label className="block text-sm font-medium text-gray-700">Opciones de respuesta</label>
                              <button
                                onClick={() => addOptionToStep(selectedStep)}
                                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                Añadir opción
                              </button>
                            </div>
                            <div className="space-y-2">
                              {(editingDiagnostic.isNew ? newDiagnostic.pasos : editingDiagnostic.pasos)[selectedStep]?.opciones.map((opcion, i) => (
                                <div key={i} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                                  <span className="text-sm font-medium text-gray-500 w-6">{i + 1}.</span>
                                  <input
                                    type="text"
                                    value={opcion.texto}
                                    onChange={(e) => updateStepOption(selectedStep, i, 'texto', e.target.value)}
                                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                    placeholder="Texto de la opción"
                                  />
                                  <ArrowRight className="w-4 h-4 text-gray-400" />
                                  <select
                                    value={opcion.siguiente}
                                    onChange={(e) => updateStepOption(selectedStep, i, 'siguiente', e.target.value)}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm min-w-[140px]"
                                  >
                                    <option value="">-- Siguiente paso --</option>
                                    <option value="FIN_RESUELTO">Fin - Resuelto</option>
                                    <option value="FIN_ESCALAR">Fin - Escalar a SAT</option>
                                    {Object.keys(editingDiagnostic.isNew ? newDiagnostic.pasos : editingDiagnostic.pasos)
                                      .filter(s => s !== selectedStep)
                                      .map(s => (
                                        <option key={s} value={s}>{s === 'inicio' ? 'Inicio' : s.replace('_', ' ')}</option>
                                      ))
                                    }
                                  </select>
                                  {(editingDiagnostic.isNew ? newDiagnostic.pasos : editingDiagnostic.pasos)[selectedStep]?.opciones.length > 2 && (
                                    <button
                                      onClick={() => removeOptionFromStep(selectedStep, i)}
                                      className="p-1 text-red-400 hover:text-red-600"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Botones de acción */}
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setEditingDiagnostic(null);
                          resetNewDiagnostic();
                        }}
                        className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition"
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={() => {
                          const diagToSave = editingDiagnostic.isNew ? newDiagnostic : editingDiagnostic;
                          if (!diagToSave.nombre.trim()) {
                            alert('Introduce un nombre para el diagnóstico');
                            return;
                          }
                          saveDiagnostic(diagToSave);
                        }}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                      >
                        <Save className="w-4 h-4" />
                        Guardar Diagnóstico
                      </button>
                    </div>
                  </div>
                ) : (
                  /* ========== LISTA DE DIAGNÓSTICOS ========== */
                  <div className="space-y-3">
                    {diagnostics.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <Wrench className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p className="text-gray-600">No hay diagnósticos creados</p>
                        <p className="text-sm">Crea tu primer diagnóstico guiado para el SAT</p>
                      </div>
                    ) : (
                      diagnostics.map((diag) => (
                        <div key={diag.id} className="border border-gray-200 rounded-xl p-4 hover:border-blue-300 transition">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-gray-900">{diag.nombre}</h3>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {diag.keywords?.map((kw, i) => (
                                  <span key={i} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">
                                    {kw}
                                  </span>
                                ))}
                              </div>
                              <p className="text-sm text-gray-500 mt-2">
                                {Object.keys(diag.pasos || {}).length} pasos configurados
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setEditingDiagnostic(diag);
                                  setSelectedStep('inicio');
                                }}
                                className="p-2 hover:bg-blue-50 rounded-lg text-blue-600"
                                title="Editar"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteDiagnostic(diag.id)}
                                className="p-2 hover:bg-red-50 rounded-lg text-red-500"
                                title="Eliminar"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === 'datos' ? (
          /* ========== PESTAÑA DATOS ========== */
          <div className="flex-1 p-4 overflow-auto">
            <div className="max-w-4xl mx-auto space-y-6">
              {/* Estadísticas de inyección - Responsive */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-3 md:p-4 text-white">
                  <p className="text-xs md:text-sm opacity-80">Inyectando desde</p>
                  <p className="text-sm md:text-lg font-bold">{formatDate(uploadStats.injectionStartDate).split(',')[0]}</p>
                  <p className="text-xs opacity-70 mt-1">{Math.floor((Date.now() - new Date(uploadStats.injectionStartDate)) / 86400000)} días</p>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-3 md:p-4 text-white">
                  <p className="text-xs md:text-sm opacity-80">Inyectados</p>
                  <p className="text-xl md:text-2xl font-bold">{uploadStats.totalInjected}</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-3 md:p-4 text-white">
                  <p className="text-xs md:text-sm opacity-80">Subidos</p>
                  <p className="text-xl md:text-2xl font-bold">{uploadStats.totalUploaded}</p>
                </div>
                <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-3 md:p-4 text-white">
                  <p className="text-xs md:text-sm opacity-80">En cola</p>
                  <p className="text-xl md:text-2xl font-bold">{uploadingFiles.length}</p>
                </div>
              </div>

              {/* Cola de subida (SUBIENDO) */}
              {uploadingFiles.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-orange-200">
                  <h3 className="text-lg font-semibold text-orange-600 mb-4 flex items-center gap-2">
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    SUBIENDO ({uploadingFiles.length}) - Ordenado por prioridad
                  </h3>
                  <div className="space-y-3">
                    {uploadingFiles.map((file, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {/* Prioridad */}
                          <span className={`px-2 py-1 rounded-lg font-bold text-sm ${
                            file.priority >= 8 ? 'bg-red-100 text-red-700' :
                            file.priority >= 5 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            P{file.priority}
                          </span>
                          {getFileIcon(file.type)}
                          <div>
                            <p className="font-medium text-gray-900">{file.name}</p>
                            <p className="text-sm text-gray-500">
                              {getDepartmentInfo(file.department).icon} {getDepartmentInfo(file.department).name} •
                              <span className={`ml-1 ${file.tipo === 'logica' ? 'text-purple-600' : 'text-blue-600'}`}>
                                {file.tipo === 'logica' ? '🧠 Lógica' : '📚 Conocimiento'}
                              </span>
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-32 h-2 bg-orange-200 rounded-full overflow-hidden">
                            <div className="h-full bg-orange-500 transition-all" style={{ width: `${file.progress || 0}%` }} />
                          </div>
                          <span className="text-sm text-orange-600 min-w-[40px]">{file.progress || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Selector de modo */}
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Subir datos para formar a XIA</h2>

                {/* Selector departamento - Responsive */}
                <div className="mb-4 md:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Departamento destino</label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {DEPARTMENTS.map(dept => (
                      <button
                        key={dept.id}
                        onClick={() => setSelectedDepartment(dept.id)}
                        className={`flex items-center justify-center gap-1 px-3 py-2 rounded-lg border-2 transition text-sm ${
                          selectedDepartment === dept.id
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span>{dept.icon}</span>
                        <span className="font-medium truncate">{dept.name}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Selector de Tipo - Responsive */}
                <div className="mb-4 md:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de contenido</label>
                  <div className="grid grid-cols-2 gap-2 md:gap-4">
                    <button
                      onClick={() => setSelectedTipo('conocimiento')}
                      className={`flex items-center gap-2 px-3 md:px-6 py-2 md:py-3 rounded-lg border-2 transition ${
                        selectedTipo === 'conocimiento'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg md:text-xl">📚</span>
                      <div className="text-left">
                        <span className="font-medium block text-sm md:text-base">Conocimiento</span>
                        <span className="text-xs text-gray-500 hidden md:block">Información, datos</span>
                      </div>
                    </button>
                    <button
                      onClick={() => setSelectedTipo('logica')}
                      className={`flex items-center gap-2 px-3 md:px-6 py-2 md:py-3 rounded-lg border-2 transition ${
                        selectedTipo === 'logica'
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="text-lg md:text-xl">🧠</span>
                      <div className="text-left">
                        <span className="font-medium block text-sm md:text-base">Lógica</span>
                        <span className="text-xs text-gray-500 hidden md:block">Procedimientos, reglas</span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Selector de Prioridad - Responsive */}
                <div className="mb-4 md:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prioridad
                    <span className="ml-1 md:ml-2 text-xs font-normal text-gray-500">(10 = máxima)</span>
                  </label>
                  <div className="flex items-center gap-2 md:gap-3">
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={selectedPriority}
                      onChange={(e) => setSelectedPriority(parseInt(e.target.value))}
                      className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                    />
                    <span className={`px-3 md:px-4 py-1.5 md:py-2 rounded-lg font-bold text-base md:text-lg min-w-[50px] md:min-w-[60px] text-center ${
                      selectedPriority >= 8 ? 'bg-red-100 text-red-700' :
                      selectedPriority >= 5 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {selectedPriority}
                    </span>
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-400">
                    <span>Baja</span>
                    <span>Normal</span>
                    <span>Urgente</span>
                  </div>
                </div>

                {/* Tabs de modo - Responsive */}
                <div className="grid grid-cols-3 gap-2 mb-4 md:mb-6">
                  <button
                    onClick={() => setUploadMode('file')}
                    className={`flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-lg transition text-sm ${
                      uploadMode === 'file' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <File className="w-4 h-4" />
                    <span className="hidden sm:inline">Archivos</span>
                    <span className="sm:hidden">Archivo</span>
                  </button>
                  <button
                    onClick={() => setUploadMode('drive')}
                    className={`flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-lg transition text-sm ${
                      uploadMode === 'drive' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <FolderOpen className="w-4 h-4" />
                    <span>Drive</span>
                  </button>
                  <button
                    onClick={() => setUploadMode('url')}
                    className={`flex items-center justify-center gap-1 md:gap-2 px-2 md:px-4 py-2 rounded-lg transition text-sm ${
                      uploadMode === 'url' ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Link className="w-4 h-4" />
                    <span>URL</span>
                  </button>
                </div>

                {/* Contenido según modo */}
                {uploadMode === 'file' && (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-4 md:p-8 text-center hover:border-blue-400 transition">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.docx,.doc,.xlsx,.xls,.epub,.mobi,.txt,.md,.azw,.azw3,.fb2"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="w-10 md:w-12 h-10 md:h-12 text-gray-400 mx-auto mb-3 md:mb-4" />
                      <p className="text-base md:text-lg font-medium text-gray-700">Toca para seleccionar archivos</p>
                      <p className="text-xs md:text-sm text-gray-500 mt-2">PDF, Word, Excel, EPUB, MOBI, TXT</p>
                      <p className="text-xs text-gray-400 mt-1 hidden md:block">También: AZW, AZW3, FB2 (ebooks)</p>
                    </label>
                  </div>
                )}

                {uploadMode === 'drive' && (
                  <div className="space-y-4">
                    {/* Estado de credenciales */}
                    <div className={`p-3 rounded-lg flex items-center justify-between ${driveStatus.hasCredentials ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                      <div className="flex items-center gap-2">
                        {driveStatus.hasCredentials ? (
                          <>
                            <Check className="w-5 h-5 text-green-600" />
                            <span className="text-sm text-green-700">API de Google Drive configurada</span>
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                            <span className="text-sm text-yellow-700">Credenciales de Drive no configuradas</span>
                          </>
                        )}
                      </div>
                      <button
                        onClick={() => setShowDriveConfig(true)}
                        className="text-sm px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        {driveStatus.hasCredentials ? 'Cambiar' : 'Configurar'}
                      </button>
                    </div>

                    {/* Modal de configuración */}
                    {showDriveConfig && (
                      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                          <h3 className="text-lg font-bold mb-4">Configurar Google Drive API</h3>
                          <div className="space-y-4">
                            <div className="p-4 bg-blue-50 rounded-lg text-sm">
                              <p className="font-medium mb-2">Instrucciones:</p>
                              <ol className="list-decimal ml-4 space-y-1">
                                <li>Ve a <a href="https://console.cloud.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Google Cloud Console</a></li>
                                <li>Crea un proyecto o selecciona uno existente</li>
                                <li>Activa la API de Google Drive</li>
                                <li>Crea una Service Account</li>
                                <li>Descarga el JSON de credenciales</li>
                                <li>Comparte la carpeta de Drive con el email de la Service Account</li>
                              </ol>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                JSON de Service Account
                              </label>
                              <textarea
                                value={driveCredentials}
                                onChange={(e) => setDriveCredentials(e.target.value)}
                                placeholder='{"type": "service_account", "project_id": "...", ...}'
                                className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg font-mono text-sm"
                              />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                onClick={() => setShowDriveConfig(false)}
                                className="px-4 py-2 text-gray-600 hover:text-gray-800"
                              >
                                Cancelar
                              </button>
                              <button
                                onClick={saveDriveCredentials}
                                disabled={!driveCredentials.trim()}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                              >
                                Guardar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Input de URL */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">URL de Google Drive</label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={driveUrl}
                          onChange={(e) => setDriveUrl(e.target.value)}
                          placeholder="https://drive.google.com/drive/folders/..."
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          disabled={!driveStatus.hasCredentials}
                        />
                        <button
                          onClick={handleDriveImport}
                          disabled={!driveUrl.trim() || !driveStatus.hasCredentials}
                          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Importar
                        </button>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">
                        Escanea archivos Word/PDF, extrae texto, URLs y pasa por antivirus
                      </p>
                    </div>

                    {/* Estado del procesamiento activo */}
                    {driveStatus.active && (
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Loader className="w-5 h-5 text-purple-600 animate-spin" />
                          <span className="font-medium text-purple-700">Procesando carpeta...</span>
                        </div>
                        {driveStatus.progress && (
                          <p className="text-sm text-purple-600">
                            {driveStatus.progress.stage === 'processing' ?
                              `Archivo ${driveStatus.progress.current}/${driveStatus.progress.total}: ${driveStatus.progress.fileName}` :
                              driveStatus.progress.stage
                            }
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {uploadMode === 'url' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">URL de documento</label>
                      <div className="flex gap-2">
                        <input
                          type="url"
                          value={driveUrl}
                          onChange={(e) => setDriveUrl(e.target.value)}
                          placeholder="https://ejemplo.com/documento.pdf"
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={handleDriveImport}
                          disabled={!driveUrl.trim()}
                          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Importar
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Progreso de subida */}
                {uploadProgress && (
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-blue-700">
                        {uploadProgress.status === 'complete' ? '¡Completado!' : `Procesando: ${uploadProgress.file}`}
                      </span>
                      <span className="text-sm text-blue-600">{uploadProgress.current}/{uploadProgress.total}</span>
                    </div>
                    <div className="w-full h-2 bg-blue-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                      />
                    </div>
                    {uploadProgress.status === 'complete' && (
                      <p className="text-sm text-green-600 mt-2">✓ Archivo procesado. Se generarán 50+ preguntas automáticamente.</p>
                    )}
                  </div>
                )}
              </div>

              {/* Archivos SUBIDO */}
              {uploadedFiles.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-green-200">
                  <h3 className="text-lg font-semibold text-green-600 mb-4 flex items-center gap-2">
                    <Check className="w-5 h-5" />
                    SUBIDO ({uploadedFiles.length})
                  </h3>
                  <div className="space-y-3">
                    {uploadedFiles.map(file => (
                      <div key={file.id} className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {/* Prioridad */}
                          <span className={`px-2 py-1 rounded-lg font-bold text-xs ${
                            file.priority >= 8 ? 'bg-red-100 text-red-700' :
                            file.priority >= 5 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            P{file.priority}
                          </span>
                          {getFileIcon(file.type)}
                          <div>
                            <p className="font-medium text-gray-900">{file.name}</p>
                            <p className="text-sm text-gray-500">
                              {getDepartmentInfo(file.department).icon} {getDepartmentInfo(file.department).name} •
                              <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${file.tipo === 'logica' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                {file.tipo === 'logica' ? '🧠 Lógica' : '📚 Conocimiento'}
                              </span>
                              • {file.questionsGenerated} preguntas
                            </p>
                            <p className="text-xs text-gray-400">
                              📄 Procedencia: {file.procedencia?.startsWith('http') ? (
                                <a href={file.procedencia} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                                  {file.procedencia?.substring(0, 40)}{file.procedencia?.length > 40 ? '...' : ''}
                                </a>
                              ) : (
                                <span>{file.procedencia?.substring(0, 40)}{file.procedencia?.length > 40 ? '...' : ''}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          ✓ Procesado
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ========== PESTAÑA LOGS ========== */
          <div className="flex-1 p-2 md:p-4 overflow-auto">
            <div className="max-w-7xl mx-auto">
              {/* Filtros - Responsive */}
              <div className="bg-white rounded-xl shadow-sm p-3 md:p-4 mb-4">
                <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-4">
                  <div className="flex items-center gap-2 mb-2 md:mb-0">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Filtros:</span>
                  </div>

                  <div className="grid grid-cols-2 md:flex gap-2">
                    <select
                      value={filters.department}
                      onChange={(e) => setFilters({ ...filters, department: e.target.value })}
                      className="px-2 md:px-3 py-2 border border-gray-300 rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Todos</option>
                      {DEPARTMENTS.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.icon} {dept.name}</option>
                      ))}
                    </select>

                    <select
                      value={filters.learned}
                      onChange={(e) => setFilters({ ...filters, learned: e.target.value })}
                      className="px-2 md:px-3 py-2 border border-gray-300 rounded-lg text-xs md:text-sm focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">Estado</option>
                      <option value="true">✅ Sí</option>
                      <option value="false">❌ No</option>
                    </select>
                  </div>

                  <button
                    onClick={fetchLogs}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition text-sm w-full md:w-auto"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Actualizar
                  </button>
                </div>
              </div>

              {/* Tabla de logs */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Fecha</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Departamento</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tema</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Tipo</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Procedencia</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Asimilado</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Preguntas</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Estado</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {logsLoading ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                            <RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />
                            Cargando logs...
                          </td>
                        </tr>
                      ) : logs.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="px-4 py-8 text-center text-gray-500">
                            No hay registros de formación
                          </td>
                        </tr>
                      ) : (
                        logs.map((log) => {
                          const dept = getDepartmentInfo(log.department);
                          const tipoInfo = KNOWLEDGE_TYPES.find(t => t.id === log.tipo) || KNOWLEDGE_TYPES[0];
                          return (
                            <tr key={log.id} className={`hover:bg-gray-50 ${log.paused ? 'bg-yellow-50' : ''}`}>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatDate(log.date)}
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 text-sm font-medium">
                                  <span>{dept.icon}</span>
                                  <span>{dept.name}</span>
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {editingLog?.id === log.id ? (
                                  <input
                                    type="text"
                                    value={editingLog.topic}
                                    onChange={(e) => setEditingLog({ ...editingLog, topic: e.target.value })}
                                    className="w-full px-2 py-1 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500"
                                  />
                                ) : (
                                  log.topic
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                                  log.tipo === 'logica' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                }`}>
                                  {tipoInfo.icon} {tipoInfo.name}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                                {log.procedencia?.startsWith('http') ? (
                                  <a href={log.procedencia} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline" title={log.procedencia}>
                                    🔗 {log.procedencia?.substring(0, 30)}{log.procedencia?.length > 30 ? '...' : ''}
                                  </a>
                                ) : (
                                  <span title={log.procedencia} className="cursor-help">
                                    📄 {log.procedencia?.substring(0, 30)}{log.procedencia?.length > 30 ? '...' : ''}
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  {log.learned ? (
                                    <span className="text-green-600">✅</span>
                                  ) : (
                                    <span className="text-red-600">❌</span>
                                  )}
                                  <span className="text-sm text-gray-600">{log.percentage}%</span>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-center">
                                {log.hasQuestions ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                                    ✅ {log.questionsCount}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">
                                    ❌ No
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {log.paused ? (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
                                    <Pause className="w-3 h-3" /> Pausado
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                                    <Play className="w-3 h-3" /> Activo
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-1">
                                  {editingLog?.id === log.id ? (
                                    <>
                                      <button onClick={handleSaveEdit} className="p-1.5 text-green-600 hover:bg-green-100 rounded-lg" title="Guardar">
                                        <Check className="w-4 h-4" />
                                      </button>
                                      <button onClick={() => setEditingLog(null)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Cancelar">
                                        <X className="w-4 h-4" />
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      {log.hasQuestions && (
                                        <button
                                          onClick={() => setViewingQuestions(log)}
                                          className="p-1.5 text-purple-600 hover:bg-purple-100 rounded-lg"
                                          title="Ver preguntas"
                                        >
                                          <EyeIcon className="w-4 h-4" />
                                        </button>
                                      )}
                                      <button onClick={() => setEditingLog(log)} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-lg" title="Editar">
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button
                                        onClick={() => handlePauseToggle(log)}
                                        className={`p-1.5 rounded-lg ${log.paused ? 'text-green-600 hover:bg-green-100' : 'text-yellow-600 hover:bg-yellow-100'}`}
                                        title={log.paused ? 'Reactivar' : 'Pausar'}
                                      >
                                        {log.paused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                                      </button>
                                      <button
                                        onClick={() => { setDeleteConfirm(log); setDeleteStep(0); }}
                                        className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg"
                                        title="Eliminar"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Paginación */}
                <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm text-gray-600">Página {page} de {totalPages}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de preguntas */}
        {viewingQuestions && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{viewingQuestions.topic}</h3>
                  <p className="text-sm text-gray-500">{viewingQuestions.questionsCount} preguntas realizadas</p>
                </div>
                <button onClick={() => setViewingQuestions(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 overflow-y-auto max-h-[60vh]">
                <div className="space-y-2">
                  {viewingQuestions.questions?.map((q, i) => (
                    <div key={q.id} className={`p-3 rounded-lg ${q.correct ? 'bg-green-50' : 'bg-red-50'}`}>
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm text-gray-700">{q.question}</p>
                        <span className={`shrink-0 px-2 py-0.5 rounded text-xs ${q.correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'}`}>
                          {q.correct ? '✓ Correcta' : '✗ Incorrecta'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmación de eliminación */}
        {deleteConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {deleteStep === 0 ? '¿Eliminar este conocimiento?' : '¿Estás seguro?'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {deleteStep === 0 ? 'XIA dejará de usar esta información' : 'Esta acción no se puede deshacer'}
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm font-medium text-gray-900">{deleteConfirm.topic}</p>
                <p className="text-xs text-gray-500">
                  {getDepartmentInfo(deleteConfirm.department).icon} {getDepartmentInfo(deleteConfirm.department).name}
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setDeleteConfirm(null); setDeleteStep(0); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className={`px-4 py-2 rounded-lg ${deleteStep === 0 ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-red-500 hover:bg-red-600'} text-white`}
                >
                  {deleteStep === 0 ? 'Sí, eliminar' : 'Confirmar eliminación'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Pantalla de login
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="bg-white px-8 py-10 text-center border-b border-gray-100">
            <div className="w-28 h-28 mx-auto mb-4">
              <img src="/xia-logo.png" alt="XIA" className="w-full h-full object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-blue-600">XIA</h1>
            <p className="text-gray-500 mt-2">Asistente Inteligente de Manantial</p>
          </div>

          <div className="p-8">
            <form onSubmit={handleLogin} className="space-y-5">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Usuario</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Introduce tu usuario"
                    autoFocus
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Introduce tu contraseña"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 shadow-lg"
              >
                Acceder a XIA
              </button>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              <p>© 2026 Manantial / Infoticketing</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
